import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

export default function Thread() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const decoded = decodeURIComponent(threadId);

  const load = () => api.get(`/threads/${encodeURIComponent(decoded)}`)
    .then((r) => setMsgs(r.data))
    .catch(() => setMsgs([]))
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const other = msgs.length
    ? (msgs[0].from_id === user?.id ? msgs[0].to_name : msgs[0].from_name)
    : "Conversation";
  const itemId = msgs[0]?.item_id;
  const itemTitle = msgs[0]?.item_title;

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/threads/${encodeURIComponent(decoded)}/reply`, { body });
      setBody("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-3xl mx-auto px-5 sm:px-8 py-8 flex flex-col">
      <Link to="/messages" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0F0F12] mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" /> All messages
      </Link>
      <div className="mb-4">
        <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-medium">{other}</h1>
        {itemId && (
          <Link to={`/items/${itemId}`} className="text-sm text-[#7C3AED] hover:underline">
            About "{itemTitle}" →
          </Link>
        )}
      </div>

      <div className="flex-1 bg-white border border-[#E8E5F0] rounded-2xl p-4 sm:p-6 overflow-y-auto min-h-[300px] max-h-[60vh]" data-testid="thread-msgs">
        {loading ? (
          <div className="text-[#6B7280]">Loading…</div>
        ) : msgs.length === 0 ? (
          <div className="text-[#6B7280] text-center py-10">No messages.</div>
        ) : (
          <ul className="space-y-3">
            {msgs.map((m) => {
              const mine = m.from_id === user?.id;
              return (
                <li key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${mine ? "bg-[#7C3AED] text-white" : "bg-[#F4F0FB] text-[#0F0F12]"}`}>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#6B7280]"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </li>
              );
            })}
            <div ref={endRef} />
          </ul>
        )}
      </div>

      <form onSubmit={send} className="mt-4 flex gap-2">
        <Input data-testid="thread-input" value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={`Message ${other}…`} className="h-11 rounded-full" />
        <Button type="submit" disabled={sending || !body.trim()}
          className="h-11 rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white px-5"
          data-testid="thread-send">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
